import { FillsGraphTimeSeriesData } from 'communication/types/fillsGraph/FillsGraphTimeSeriesData';
import { OrderFill } from './fillsGraphData';
import { TimeSeriesDisableRange } from 'utils/timeseries/TimeSeriesDisableRange';
import { createEmptyBuckets, findNextBucket } from 'utils/timeseries/bucket';
import { getItemTime } from 'utils/timeseries/timeSeriesUtils';
import { TimeSeriesChartDataItem } from 'utils/components/TimeSeriesChart/TimeSeriesChartData';
import isNil from 'lodash/isNil';

export interface FillsData {
    data: FillsGraphTimeSeriesData[];
    bucketSizeInMinutes: number;
}

interface BucketedDataItem {
    startTime: number;
    midTime: number;
    endTime: number;
    endTimeDisplay: number;
    price: number;
    actualVolume: number;
    expectedVolume: number;
    fillVolume: number;
    candle: {
        open: number;
        high: number;
        low: number;
        close: number;
    };
    afterBucketWithEndTime: boolean;
}

export interface BucketedData extends BucketedDataItem {
    percentOfMarketVolume: number;
    averagePercentOfMarketVolume: number;
}

export function bucket(
    bucketSizeInMinutes: number,
    sortedData: FillsGraphTimeSeriesData[],
    breaks: TimeSeriesDisableRange[],
    endTimestamp: number | undefined
): BucketedData[] {
    const bucketedData = createEmptyBuckets<BucketedData>(
        bucketSizeInMinutes,
        sortedData,
        breaks,
        buildEmptyBucket
    );

    let currentBucket: BucketedDataItem | undefined = bucketedData[0];
    let sumOfPriceTimesFill = 0;
    let totalDealt = 0;

    for (const d of sortedData) {
        if (!currentBucket) {
            break;
        }
        const actualVolume = d.v ? d.v : 0;
        const expectedVolume = d.vp ? d.vp : 0;
        const quantityDealt = d.bt || d.st || 0;
        const price = d.bp || d.sp || 0;

        sumOfPriceTimesFill += price * quantityDealt;
        totalDealt += quantityDealt;

        const avgPrice = totalDealt ? sumOfPriceTimesFill / totalDealt : 0;

        const dataTime = getItemTime(d);

        if (dataTime <= currentBucket.endTime) {
            currentBucket.price = avgPrice;
            currentBucket.actualVolume =
                currentBucket.actualVolume + actualVolume;
            currentBucket.expectedVolume =
                currentBucket.expectedVolume + expectedVolume;
            currentBucket.fillVolume = totalDealt;
        } else {
            currentBucket = findNextBucket(bucketedData, currentBucket);
            if (currentBucket) {
                currentBucket.price = price;
                currentBucket.actualVolume = actualVolume;
                currentBucket.expectedVolume = expectedVolume;
                currentBucket.fillVolume = quantityDealt;

                sumOfPriceTimesFill = price * quantityDealt;

                totalDealt = quantityDealt;
            }
        }

        if (currentBucket && !isNil(d.o)) {
            if (!currentBucket.candle) {
                currentBucket.candle = {
                    open: d.o,
                    high: d.h,
                    low: d.l,
                    close: d.c,
                };
            } else {
                currentBucket.candle.high = Math.max(
                    currentBucket.candle.high,
                    d.h
                );
                currentBucket.candle.low = Math.min(
                    currentBucket.candle.low,
                    d.l
                );
                currentBucket.candle.close = d.c;
            }
        }
    }

    // Trim trailing buckets without any values to ensure that the last bin label works...
    let lastWithValue = -1;
    for (let i = bucketedData.length - 1; i > -1; i--) {
        const item = bucketedData[i];
        if (
            item.expectedVolume > 0 ||
            item.actualVolume > 0 ||
            item.fillVolume > 0 ||
            item.price > 0
        ) {
            lastWithValue = i;
            break;
        }
    }

    if (lastWithValue < bucketedData.length - 1) {
        bucketedData.splice(lastWithValue + 1);
    }

    const bucketedArray: BucketedData[] = bucketedData.map((value) => {
        return {
            startTime: value.startTime,
            midTime: value.midTime,
            endTime: value.endTime,
            endTimeDisplay: value.endTimeDisplay,
            price: value.price,
            actualVolume: value.actualVolume,
            expectedVolume: value.expectedVolume,
            fillVolume: value.fillVolume,
            percentOfMarketVolume:
                value.actualVolume > 0
                    ? (value.fillVolume * 100) / value.actualVolume
                    : 0,
            averagePercentOfMarketVolume: 0,
            candle: value.candle,
            afterBucketWithEndTime: !!(
                endTimestamp && endTimestamp < value.startTime
            ),
        };
    });

    calculateAveragePercentOfMarketVolume(bucketedArray);

    return bucketedArray;
}

export function mergeBucketedData(
    sortedData: OrderFill[],
    bucketedData: BucketedData[]
) {
    for (const bd of bucketedData) {
        const index = sortedData.findIndex((d) => d.time >= bd.startTime);
        if (index >= 0) {
            const item = {
                ...sortedData[index],
                time: bd.startTime,
                bucket: bd,
            };
            if (sortedData[index].time === bd.startTime) {
                sortedData[index] = item;
            } else {
                sortedData.splice(index, 0, item);
            }
        } else {
            sortedData.push({ time: bd.startTime, bucket: bd });
        }
    }
}

export function findBucket(
    data?: OrderFill[],
    time?: number
): BucketedData | undefined {
    if (data === undefined || time === undefined) {
        return undefined;
    }
    const orderFill = data
        .filter((d) => !!d.bucket)
        .find((d) => {
            // "time" will have secs e.g. Thu Sep 19 2019 08:29:38 whilst buckets are rounded to mins e.g. end time may be Thu Sep 19 2019 08:29:00
            // So we add a min to end end time and use less than
            const startOfNext = d.bucket!.endTime + 60000;
            return time >= d.bucket!.startTime && time < startOfNext;
        });

    return orderFill && orderFill.bucket;
}

function calculateAveragePercentOfMarketVolume(
    bucketedData: BucketedData[]
): void {
    const reducer = (
        volumeSums: {
            fillSinceBucketWithFirstFill: number;
            volumeSinceBucketWithFirstFill: number;
            earlierBucketHasFill: boolean;
        },
        currentValue: BucketedData
    ) => {
        if (currentValue.fillVolume > 0) {
            volumeSums.earlierBucketHasFill = true;
        }

        if (volumeSums.earlierBucketHasFill) {
            if (!currentValue.afterBucketWithEndTime) {
                // IND2-2865 - remains constant after bucket with end time
                volumeSums.fillSinceBucketWithFirstFill +=
                    currentValue.fillVolume;
                volumeSums.volumeSinceBucketWithFirstFill +=
                    currentValue.actualVolume;
            }

            if (
                volumeSums.fillSinceBucketWithFirstFill > 0 &&
                volumeSums.volumeSinceBucketWithFirstFill > 0
            ) {
                currentValue.averagePercentOfMarketVolume =
                    (volumeSums.fillSinceBucketWithFirstFill /
                        volumeSums.volumeSinceBucketWithFirstFill) *
                    100;
            }
        }
        return volumeSums;
    };

    bucketedData.reduce(reducer, {
        fillSinceBucketWithFirstFill: 0,
        volumeSinceBucketWithFirstFill: 0,
        earlierBucketHasFill: false,
    });
}

function buildEmptyBucket(): Partial<BucketedData> {
    return {
        price: 0,
        actualVolume: 0,
        expectedVolume: 0,
        fillVolume: 0,
    };
}

export function ensureEndOfLastBucketWithActualVolumeOrFillVolumeIsInTimeDomain(
    data: TimeSeriesChartDataItem[],
    buckets: BucketedData[]
): number | undefined {
    if (data.length === 0 || buckets.length === 0) {
        return;
    }

    const endTime = data[data.length - 1].time;

    const bucketsWithActualVolumeOrFillVolume = buckets.filter(
        (b) => !!b.actualVolume || !!b.fillVolume
    );
    if (bucketsWithActualVolumeOrFillVolume.length === 0) {
        return;
    }

    const lastBucketWithActualVolumeOrFillVolume =
        bucketsWithActualVolumeOrFillVolume[
            bucketsWithActualVolumeOrFillVolume.length - 1
        ];

    const lastBucketEndTime = lastBucketWithActualVolumeOrFillVolume.endTime;
    if (lastBucketEndTime > endTime) {
        // Expand the time domain to include all of the last bucket
        data.push({ time: lastBucketEndTime });
        return lastBucketEndTime;
    }
}
